import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  InputAdornment,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  LocalShipping as ParcelIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  QrCode as TrackingIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Inventory as ReceiverIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import {
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
  applyApiValidationErrors,
} from '../../../utils/formValidationFeedback';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import { SUCCESS } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canModifyParcel, canSetParcelWorkflowOnCreate } from '../../../utils/parcelPermissions';
import TextField from '@mui/material/TextField';
import SearchableSelect from '../../common/SearchableSelect';
import { inputRootSx, pickerFieldSx } from '../../common/formFieldSx';

const PARCEL_VALIDATION_FIELDS = new Set([
  'buyer_id', 'parcel_type', 'tracking_number', 'phone_number', 'location',
  'description', 'penalty_date', 'status', 'receiver_id', 'received_date', 'additional_note',
]);

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

// ─── Shared CSS injected into <head> ──────────────────────────────────────────
// Matches MaintenanceForm exactly: same FORM_CSS + PICKER_CSS split,
// same animations, same autofill override, same calendar popup theming.
const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }

  .MuiOutlinedInput-input,
  .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
`;

const PICKER_CSS = `
  .MuiPickersPopper-root .MuiPaper-root,
  .MuiDateCalendar-root,
  .MuiPickersLayout-root {
    background: #0a1628 !important;
    color: #E2E8F0 !important;
    border: 1px solid rgba(56,189,248,0.22) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.65) !important;
  }
  .MuiDayCalendar-weekDayLabel {
    color: rgba(56,189,248,0.65) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.65rem !important;
    font-weight: 600 !important;
    letter-spacing: 1px !important;
  }
  .MuiPickersDay-root {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
    background: transparent !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersDay-root:hover {
    background: rgba(56,189,248,0.18) !important;
    color: #38BDF8 !important;
    box-shadow: 0 0 8px rgba(56,189,248,0.3) !important;
  }
  .MuiPickersDay-root.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    border: 2px solid #38BDF8 !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.6) !important;
  }
  .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid rgba(56,189,248,0.65) !important;
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.08) !important;
  }
  .MuiPickersDay-root.Mui-disabled,
  .MuiPickersDay-root[aria-disabled="true"] {
    color: rgba(100, 116, 139, 0.45) !important;
    -webkit-text-fill-color: rgba(100, 116, 139, 0.45) !important;
    background: rgba(15, 23, 42, 0.45) !important;
    opacity: 1 !important;
    text-decoration: none !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
  }
  .MuiPickersDay-root.Mui-disabled:hover,
  .MuiPickersDay-root[aria-disabled="true"]:hover {
    color: rgba(100, 116, 139, 0.45) !important;
    -webkit-text-fill-color: rgba(100, 116, 139, 0.45) !important;
    background: rgba(15, 23, 42, 0.45) !important;
    box-shadow: none !important;
  }
  .MuiPickersDay-root.Mui-disabled.MuiPickersDay-today,
  .MuiPickersDay-root[aria-disabled="true"].MuiPickersDay-today {
    border-color: rgba(71, 85, 105, 0.5) !important;
    color: rgba(100, 116, 139, 0.5) !important;
    background: rgba(15, 23, 42, 0.5) !important;
  }
  .MuiPickersCalendarHeader-label {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
  }
  .MuiPickersArrowSwitcher-button,
  .MuiPickersCalendarHeader-switchViewButton {
    color: rgba(56,189,248,0.75) !important;
  }
  .MuiPickersArrowSwitcher-button:hover,
  .MuiPickersCalendarHeader-switchViewButton:hover {
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiPickersLayout-toolbar,
  .MuiDatePickerToolbar-root {
    background: rgba(5, 13, 26, 0.96) !important;
    border-bottom: 1px solid rgba(56,189,248,0.15) !important;
  }
  .MuiPickersToolbarText-root,
  .MuiDatePickerToolbar-title {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
  }
  .MuiPickersActionBar-root {
    border-top: 1px solid rgba(56,189,248,0.12) !important;
    background: rgba(5, 13, 26, 0.9) !important;
  }
  .MuiPickersActionBar-root .MuiButton-root {
    color: rgba(56,189,248,0.9) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-weight: 600 !important;
  }
  .MuiYearCalendar-root button,
  .MuiMonthCalendar-root button {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .MuiYearCalendar-root button:hover,
  .MuiMonthCalendar-root button:hover {
    background: rgba(56,189,248,0.1) !important;
    color: #38BDF8 !important;
  }
  .MuiYearCalendar-root button.Mui-selected,
  .MuiMonthCalendar-root button.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.5) !important;
    border-radius: 8px !important;
  }
  .MuiMonthCalendar-root .Mui-disabled,
  .MuiYearCalendar-root .Mui-disabled {
    color: rgba(100, 116, 139, 0.45) !important;
    opacity: 1 !important;
    background: transparent !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root,
  .MuiPickersOutlinedInput-root {
    border-radius: 10px !important;
    background: rgba(15, 31, 56, 0.7) !important;
    backdrop-filter: blur(8px) !important;
    transition: box-shadow 0.2s ease, border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.18) !important;
    transition: border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersTextField-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.4) !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.65) !important;
    border-width: 1.5px !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused,
  .MuiPickersOutlinedInput-root.Mui-focused {
    box-shadow: 0 0 0 3px rgba(56,189,248,0.12), 0 0 14px rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.875rem !important;
    color: rgba(56,189,248,0.45) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root.Mui-focused {
    color: rgba(56,189,248,0.85) !important;
    font-weight: 600 !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionStart .MuiSvgIcon-root {
    color: rgba(56,189,248,0.45) !important;
    font-size: 18px !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root {
    color: rgba(56,189,248,0.35) !important;
    padding: 6px !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root:hover {
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiSvgIcon-root {
    font-size: 17px !important;
  }
  .MuiPickersSectionList-root {
    color: rgba(226,232,240,0.85) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.9rem !important;
  }
  .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-separator {
    color: rgba(226,232,240,0.85) !important;
    -webkit-text-fill-color: rgba(226,232,240,0.85) !important;
  }
  .MuiPickersSectionList-sectionContent[data-value=""] {
    color: rgba(56,189,248,0.3) !important;
    -webkit-text-fill-color: rgba(56,189,248,0.3) !important;
  }
  .MuiPickersSectionList-section:focus .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-sectionContent:focus {
    background: rgba(56,189,248,0.15) !important;
    border-radius: 3px !important;
    color: #38BDF8 !important;
    -webkit-text-fill-color: #38BDF8 !important;
    outline: none !important;
  }
`;

const TEXT_BODY = '#E2E8F0';

const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

// ─── StyledField ──────────────────────────────────────────────────────────────
const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

// ─── makePickerSlots — identical signature to MaintenanceForm ─────────────────
// makePickerSlots(size, icon)(disabled) → slotProps object
// Uses pickerFieldSx from formFieldSx.js so the textField renders with the
// exact same neon theme / disabled-slate theme as the Maintenance date picker.
const makePickerSlots = (size = 'normal', icon = null) => (disabled = false) => ({
  textField: {
    fullWidth: true,
    size,
    InputLabelProps: { shrink: true },
    InputProps: icon
      ? {
          startAdornment: (
            <InputAdornment position="start">
              {React.cloneElement(icon, { sx: { fontSize: 18 } })}
            </InputAdornment>
          ),
        }
      : undefined,
    sx: pickerFieldSx(size, { disabled }),
  },
  popper: { placement: 'bottom-start' },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STATUS_ALLOWS_COLLECTED_DATE = 'collected';

const isCollectedStatus = (status) => status === STATUS_ALLOWS_COLLECTED_DATE;

const PHONE_PATTERN = /^[\d\-\+\(\)\s]+$/;

const applyCollectedWorkflowRules = (errors, formData, showWorkflowSection) => {
  if (!showWorkflowSection || !isCollectedStatus(formData.status)) return;
  if (!formData.receiver_id) {
    errors.receiver_id = 'Receiver is required when status is Collected';
  }
  if (!formData.received_date || !dayjs(formData.received_date).isValid()) {
    errors.received_date = 'Collected date is required when status is Collected';
  }
};

function getParcelFormErrors(formData, showWorkflowSection) {
  const errors = {};
  const tracking = (formData.tracking_number || '').trim();
  const phone = (formData.phone_number || '').trim();
  const description = (formData.description || '').trim();

  if (!tracking) errors.tracking_number = 'Tracking number is required';
  if (!phone) errors.phone_number = 'Phone number is required';
  else if (!PHONE_PATTERN.test(phone)) errors.phone_number = 'Invalid phone number';

  if (!formData.buyer_id) errors.buyer_id = 'Please select a sender';
  if (!formData.location) errors.location = 'Location is required';
  if (!formData.parcel_type) errors.parcel_type = 'Parcel type is required';
  if (showWorkflowSection && !formData.status) errors.status = 'Status is required';
  applyCollectedWorkflowRules(errors, formData, showWorkflowSection);
  return errors;
}

function normalizeParcelPayload(formData, showWorkflowSection) {
  const dateStr = (v) => {
    if (v == null || v === '') return null;
    if (dayjs.isDayjs(v)) return v.isValid() ? v.format('YYYY-MM-DD') : null;
    if (typeof v === 'string' && v.trim()) {
      const parsed = dayjs(v);
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    }
    return null;
  };
  const status = formData.status || 'pending';
  const payload = {
    buyer_id:        Number(formData.buyer_id),
    tracking_number: (formData.tracking_number || '').trim(),
    parcel_type:     formData.parcel_type,
    phone_number:    (formData.phone_number || '').trim(),
    location:        formData.location,
    description:     (formData.description || '').trim() || null,
    penalty_date:    dateStr(formData.penalty_date),
    status,
    receiver_id:     formData.receiver_id ? Number(formData.receiver_id) : null,
    received_date:   showWorkflowSection ? dateStr(formData.received_date) : null,
    additional_note: (formData.additional_note || '').trim() || null,
  };
  if (!showWorkflowSection) {
    payload.status        = 'pending';
    payload.receiver_id   = null;
    payload.received_date = null;
    payload.additional_note = null;
  } else if (!isCollectedStatus(status)) {
    payload.received_date = null;
  }
  return payload;
}

const senderPhoneForBuyer = (buyerId, users) => {
  if (!buyerId || !users?.length) return '';
  const buyer = users.find((u) => String(u.id) === String(buyerId));
  return (buyer?.phone_number || '').trim();
};

// ─── Main Component ────────────────────────────────────────────────────────────
const ParcelForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [formData, setFormData] = useState({
    buyer_id: '', parcel_type: 'package', tracking_number: '',
    phone_number: '', location: '', description: '',
    penalty_date: null, status: 'pending', receiver_id: '',
    received_date: null, additional_note: '',
  });

  const [users,                   setUsers]                   = useState([]);
  const [loading,                 setLoading]                 = useState(false);
  const [dataLoading,             setDataLoading]             = useState(false);
  const [error,                   setError]                   = useState(null);
  const [successMessage,          setSuccessMessage]          = useState('');
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [parcelRecord,            setParcelRecord]            = useState(null);
  const [collectedPickerOpen,     setCollectedPickerOpen]     = useState(false);
  const [penaltyPickerOpen, setPenaltyPickerOpen] = useState(false);

  const canSetWorkflowOnCreate = canSetParcelWorkflowOnCreate(currentUser);
  const showWorkflowSection =
    (!isEditMode && canSetWorkflowOnCreate) ||
    (isEditMode && parcelRecord && canModifyParcel(currentUser, parcelRecord));

  const canEditCollectedDate = showWorkflowSection && formData.status === STATUS_ALLOWS_COLLECTED_DATE;

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'parcel',
    userId: currentUser?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  // ── Option arrays for SearchableSelect ────────────────────────────────────
  const parcelTypeOptions = [
    { value: 'document',    label: 'Document' },
    { value: 'package',     label: 'Package' },
    { value: 'equipment',   label: 'Equipment' },
    { value: 'food',        label: 'Food Items' },
    { value: 'electronics', label: 'Electronics' },
    { value: 'other',       label: 'Other' },
  ];
  const locationOptions = [
    { value: 'OPC',     label: 'OPC (One Stop Center)' },
    { value: 'CPP',     label: 'CPP (Arked Cengal)' },
    { value: 'Angkasa', label: 'Arked Angkasa' },
    { value: 'other',   label: 'Other Location' },
  ];
  const statusOptions = [
    { value: 'pending',   label: 'Pending' },
    { value: 'arrived',   label: 'Arrived' },
    { value: 'collected', label: 'Collected' },
    { value: 'returned',  label: 'Returned' },
  ];
  // Built once users are loaded
  const buyerOptions    = [
    ...users.map((u) => ({ value: u.id, label: u.name })),
  ];
  const receiverOptions = [
    ...users.map((u) => ({ value: u.id, label: u.name })),
  ];

  // ── DatePicker slot factory (identical to MaintenanceForm) ─────────────────
  const makeDatePickerSlots = makePickerSlots('normal', <CalendarTodayIcon />);

  // ── Inject CSS (FORM_CSS + PICKER_CSS, same approach as MaintenanceForm) ───
  useEffect(() => {
    const cssId = 'parcelform-ds-css';
    let el = document.getElementById(cssId);
    if (!el) { el = document.createElement('style'); el.id = cssId; document.head.appendChild(el); }
    el.innerHTML = FORM_CSS + PICKER_CSS;
  }, []);

  // ── Close collected picker when status changes away from 'collected' ────────
  useEffect(() => {
    if (!canEditCollectedDate) setCollectedPickerOpen(false);
  }, [formData.status, canEditCollectedDate]);

  // ── Fetch users & parcel ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/users?active_only=1');
        setUsers(res.data?.data || res.data || []);
      } catch { setError('Failed to load users'); }
    };
    fetchUsers();
    if (isEditMode) fetchParcelData();
    else setDataLoading(false);
  }, [id]);

  // ── Pre-fill buyer and phone from current user on create ───────────────────
  useEffect(() => {
    if (isEditMode || !currentUser?.id || !users.length) return;
    setFormData((prev) => {
      if (prev.buyer_id !== '' && prev.buyer_id != null) {
        if (prev.phone_number.trim()) return prev;
        const phone = senderPhoneForBuyer(prev.buyer_id, users);
        return phone ? { ...prev, phone_number: phone } : prev;
      }
      const match = users.find((u) => String(u.id) === String(currentUser.id));
      if (!match) return prev;
      const phone = senderPhoneForBuyer(match.id, users);
      return {
        ...prev,
        buyer_id: match.id,
        phone_number: phone || prev.phone_number,
      };
    });
  }, [isEditMode, currentUser?.id, users]);

  // ── Guard: redirect if user cannot edit this parcel ────────────────────────
  useEffect(() => {
    if (!isEditMode || !parcelRecord || !currentUser) return;
    if (!canModifyParcel(currentUser, parcelRecord)) navigate('/parcels', { replace: true });
  }, [isEditMode, parcelRecord, currentUser, navigate]);

  const fetchParcelData = async () => {
    setDataLoading(true);
    try {
      const res  = await api.get(`/parcels/${id}`);
      const data = res.data?.data || res.data;
      setParcelRecord(data);
      setFormData({
        buyer_id:        data.buyer_id        || '',
        parcel_type:     data.parcel_type     || 'package',
        tracking_number: data.tracking_number || '',
        phone_number:    data.phone_number    || '',
        location:        data.location        || 'OPC',
        description:     data.description     || '',
        penalty_date:    data.penalty_date  ? dayjs(data.penalty_date)  : null,
        status:          data.status          || 'pending',
        receiver_id:     data.receiver_id     || '',
        received_date:   data.status === STATUS_ALLOWS_COLLECTED_DATE && data.received_date
          ? dayjs(data.received_date)
          : null,
        additional_note: data.additional_note || '',
      });
      setError(null);
    } catch { setError('Failed to fetch parcel data. Please try again.'); }
    finally   { setDataLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const collectFormErrors = () => getParcelFormErrors(formData, showWorkflowSection);

  const validateForm = () => {
    const errors = collectFormErrors();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setShowFieldErrors(true);
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setShowFieldErrors(false);
    setError(null);
    return true;
  };

  const handleApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, PARCEL_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!PARCEL_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const doSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      const payload = normalizeParcelPayload(formData, showWorkflowSection);
      if (isEditMode) await api.put(`/parcels/${id}`, payload);
      else await api.post('/parcels', payload);
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage(`Parcel ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => navigate('/parcels', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      handleApiError(err, `Failed to ${isEditMode ? 'update' : 'create'} parcel.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage('');
    if (isEditMode) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setConfirmUpdateDialogOpen(true);
      return;
    }
    setError(null);
    if (!validateForm()) return;
    doSave();
  };

  const handleConfirmUpdate = async () => {
    const errors = collectFormErrors();
    if (Object.keys(errors).length > 0) {
      setShowFieldErrors(true);
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
        setShowFieldErrors,
      });
      return;
    }
    setConfirmUpdateDialogOpen(false);
    await doSave();
  };

  if (!currentUser) return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <FeedbackMessage message="Please log in to access this page." severity="error" />
    </Container>
  );
  if (dataLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading parcel form" size={56} variant="primary" />
    </Box>
  );

  const isCollected = showWorkflowSection && isCollectedStatus(formData.status);
  const isValidCollectedDate = (v) => {
    if (v == null) return false;
    if (dayjs.isDayjs(v)) return v.isValid();
    return dayjs(v).isValid();
  };
  const canSubmit = Boolean(
    formData.tracking_number?.trim()
    && formData.phone_number?.trim()
    && formData.buyer_id
    && formData.location
    && formData.parcel_type
    && (!isCollected || (formData.receiver_id && isValidCollectedDate(formData.received_date))),
  );
  const submitDisabled = loading || !canSubmit;

  const fieldHelper = (name) => (
    showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined
  );
  const fieldHasError = (name) => showFieldErrors && !!validationErrors[name];

  // ── Picker slot configs ────────────────────────────────────────────────────
  // Penalty date: always enabled (no lock needed)
  const penaltyPickerSlots = makeDatePickerSlots(false);

  // Collected date: locked until status === 'collected', same pattern as
  // MaintenanceForm's completion date picker.
  const collectedPickerDisabled = !canEditCollectedDate;
  const collectedPickerSlots    = makeDatePickerSlots(collectedPickerDisabled);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

        <Header
          title={isEditMode ? 'Edit Parcel' : 'Create New Parcel'}
          subtitle={isEditMode ? 'Update parcel information and details' : 'Register a new parcel for tracking'}
          icon={ParcelIcon}
          showBackButton
          onBackClick={() => navigate(-1)}
          backButtonText="Back to Parcels"
        />

        <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage('')}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
        />
        <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
        />

        {formDraft.showNotice && (
          <FormDraftNotice
            savedAt={formDraft.restoredAt}
            onDiscard={formDraft.discardDraft}
            onDismiss={formDraft.dismissNotice}
          />
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ── Sender ──────────────────────────────────────────────────────── */}
            <SectionCard title="Sender" icon={<PersonIcon />} delay={0}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>

                {/* Sender / buyer — SearchableSelect */}
                <SearchableSelect
                  required
                  label="Sender"
                  value={formData.buyer_id}
                  onChange={(v) => {
                    const senderPhone = senderPhoneForBuyer(v, users);
                    setFormData((prev) => ({
                      ...prev,
                      buyer_id: v,
                      phone_number: senderPhone || prev.phone_number,
                    }));
                    if (error) setError(null);
                    if (showFieldErrors) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        if (next.buyer_id) delete next.buyer_id;
                        if (senderPhone && next.phone_number) delete next.phone_number;
                        return next;
                      });
                    }
                  }}
                  options={buyerOptions}
                  error={fieldHasError('buyer_id')}
                  helperText={fieldHelper('buyer_id')}
                  startIcon={<PersonIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  sx={{ flex: 1 }}
                />

                {/* Parcel type — SearchableSelect */}
                <SearchableSelect
                  required
                  label="Parcel Type"
                  value={formData.parcel_type}
                  onChange={(v) => handleFieldChange('parcel_type', v)}
                  options={parcelTypeOptions}
                  error={fieldHasError('parcel_type')}
                  helperText={fieldHelper('parcel_type')}
                  startIcon={<ParcelIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  sx={{ flex: 1 }}
                />
              </Box>
            </SectionCard>

            {/* ── Parcel Details ────────────────────────────────────────────── */}
            <SectionCard title="Parcel Details" icon={<TrackingIcon />} delay={0.06}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <StyledField
                    fullWidth required
                    label="Tracking Number"
                    name="tracking_number"
                    value={formData.tracking_number}
                    onChange={handleChange}
                    error={fieldHasError('tracking_number')}
                    helperText={fieldHelper('tracking_number')}
                    sx={{ flex: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TrackingIcon sx={{ color: alpha(NEON, 0.5), fontSize: 18 }} /></InputAdornment> }}
                  />
                  <StyledField
                    fullWidth required
                    label="Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    error={fieldHasError('phone_number')}
                    helperText={fieldHelper('phone_number')}
                    sx={{ flex: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2.5,
                    alignItems: 'flex-start',
                    width: '100%',
                  }}
                >
                  {/* Location */}
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <SearchableSelect
                      required
                      label="Location"
                      value={formData.location}
                      onChange={(v) => handleFieldChange('location', v)}
                      options={locationOptions}
                      error={fieldHasError('location')}
                      helperText={fieldHelper('location')}
                      startIcon={
                        <LocationIcon
                          sx={{
                            color: alpha(NEON, 0.45),
                            fontSize: 18,
                          }}
                        />
                      }
                    />
                  </Box>

                  {/* Penalty Date */}
                  <Box sx={{ flex: 1, width: '100%' }}>
                  <DatePicker
                    label="Penalty Date"
                    value={formData.penalty_date}
                    open={penaltyPickerOpen}
                    onOpen={() => setPenaltyPickerOpen(true)}
                    onClose={() => setPenaltyPickerOpen(false)}
                    onChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        penalty_date: v?.isValid() ? v : null,
                      }))
                    }
                    slotProps={{
                      ...penaltyPickerSlots,

                      textField: {
                        ...penaltyPickerSlots.textField,
                        fullWidth: true,

                        onClick: () => setPenaltyPickerOpen(true),

                        onKeyDown: (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setPenaltyPickerOpen(true);
                          }
                        },
                      },

                      openPickerButton: {
                        onClick: (e) => {
                          e.stopPropagation();
                          setPenaltyPickerOpen(true);
                        },
                      },
                    }}
                  />
                  </Box>
                </Box>

                <StyledField
                  fullWidth multiline rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  error={fieldHasError('description')}
                  helperText={fieldHelper('description')}
                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />
              </Box>
            </SectionCard>

            {/* ── Status & Receiver Information ─────────────────────────────── */}
            {showWorkflowSection && (
              <SectionCard title="Status & Receiver Information" icon={<ReceiverIcon />} delay={0.12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <SearchableSelect
                    required
                    label="Status"
                    value={formData.status}
                    onChange={(v) => {
                      setFormData((prev) => ({
                        ...prev,
                        status: v,
                        received_date: v === STATUS_ALLOWS_COLLECTED_DATE
                          ? (prev.received_date && dayjs(prev.received_date).isValid()
                            ? prev.received_date
                            : dayjs())
                          : null,
                      }));
                      if (error) setError(null);
                      if (showFieldErrors) {
                        setValidationErrors((prev) => {
                          const next = { ...prev };
                          if (next.status) delete next.status;
                          if (v !== STATUS_ALLOWS_COLLECTED_DATE) {
                            delete next.receiver_id;
                            delete next.received_date;
                          }
                          return next;
                        });
                      }
                    }}
                    options={statusOptions}
                    error={fieldHasError('status')}
                    helperText={fieldHelper('status')}
                    startIcon={<CheckCircleIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, width: '100%' }}>
                    <Box sx={{ flex: 1, width: '100%' }}>
                      <SearchableSelect
                        required={isCollected}
                        label="Receiver"
                        value={formData.receiver_id}
                        onChange={(v) => handleFieldChange('receiver_id', v)}
                        options={receiverOptions}
                        error={fieldHasError('receiver_id')}
                        helperText={fieldHelper('receiver_id')}
                        startIcon={<PersonIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                      />
                    </Box>

                    <Box sx={{ flex: 1, width: '100%' }}>
                      <DatePicker
                        key={`collected-${formData.status}`}
                        label="Collected Date"
                        value={formData.received_date ?? null}
                        maxDate={dayjs()}
                        open={collectedPickerOpen}
                        onOpen={() => canEditCollectedDate && setCollectedPickerOpen(true)}
                        onClose={() => setCollectedPickerOpen(false)}
                        onChange={(v) => {
                          setFormData((prev) => ({ ...prev, received_date: v?.isValid() ? v : null }));
                          if (error) setError(null);
                          if (showFieldErrors && validationErrors.received_date) {
                            setValidationErrors((prev) => ({ ...prev, received_date: '' }));
                          }
                        }}
                        disabled={collectedPickerDisabled}
                        slotProps={(() => {
                          const openPicker = () => {
                            if (!collectedPickerDisabled) setCollectedPickerOpen(true);
                          };
                          return {
                            ...collectedPickerSlots,
                            textField: {
                              ...collectedPickerSlots.textField,
                              required: isCollected,
                              error: fieldHasError('received_date'),
                              helperText: fieldHelper('received_date'),
                              onClick: openPicker,
                              onKeyDown: (e) => {
                                if (collectedPickerDisabled) return;
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); }
                              },
                            },
                            openPickerButton: {
                              onClick: (e) => { e.stopPropagation(); openPicker(); },
                            },
                          };
                        })()}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      background: alpha(NEON, 0.04),
                      border: `1px solid ${alpha(NEON, 0.14)}`,
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <InfoIcon sx={{ color: alpha(NEON, 0.55), flexShrink: 0, mt: 0.15, fontSize: 18 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.82), lineHeight: 1.45 }}>
                        Set status to <Box component="span" sx={{ color: NEON, fontWeight: 600 }}>Collected</Box> to unlock collected date and require a receiver.
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.72), lineHeight: 1.45 }}>
                        Collected date defaults to today when status changes to Collected; adjust it if pickup happened on another day.
                      </Typography>
                      {canEditCollectedDate && formData.received_date && (
                        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: alpha(NEON, 0.65), letterSpacing: 0.2, mt: 0.25 }}>
                          Selected: {dayjs(formData.received_date).format('dddd, D MMMM YYYY')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <StyledField
                    fullWidth multiline rows={2}
                    label="Additional Notes"
                    name="additional_note"
                    value={formData.additional_note}
                    onChange={handleChange}
                    InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                </Box>
              </SectionCard>
            )}

            <SubmitActionsCard
              statusText={
                isEditMode
                  ? 'Save changes to update this parcel.'
                  : 'Complete the form to register a new parcel.'
              }
              missingRequiredFields={isEditMode ? null : [!formData.tracking_number?.trim() && 'Tracking Number', !formData.phone_number?.trim() && 'Phone Number', !formData.buyer_id && 'Buyer', !formData.location && 'Location', !formData.parcel_type && 'Parcel Type', isCollected && !formData.receiver_id && 'Receiver', isCollected && !isValidCollectedDate(formData.received_date) && 'Collected Date'].filter(Boolean)}
              readyStatusText="All required fields are complete. Ready to register parcel."
              cancelLabel="Cancel"
              onCancel={() => navigate(-1)}
              cancelDisabled={loading}
              submitLabel={isEditMode ? 'Update Parcel' : 'Create Parcel'}
              submitDisabled={submitDisabled}
              loading={loading}
              submitStartIcon={<SaveIcon />}
              showSubmit
              submitType="submit"
              {...submitDraftProps}
            />

          </Box>
        </form>

        {isEditMode && (
          <ConfirmUpdateDialog
            open={confirmUpdateDialogOpen}
            onClose={() => {
              if (loading) return;
              setConfirmUpdateDialogOpen(false);
            }}
            onConfirm={handleConfirmUpdate}
            title="Confirm Update"
            variant="edit"
            itemType="parcel"
            itemName={formData.tracking_number}
            description="This action will update the parcel details."
            confirmLabel="Update Parcel"
            loading={loading}
            confirmDisabled={!canSubmit}
          />
        )}

      </Container>
    </LocalizationProvider>
  );
};

export default ParcelForm;
